import React from 'react';
import useIsMobile from '../../utils/useIsMobile';

const trustIcons = {
  delivery: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  return: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>,
  genuine: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  secure: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
};

const ProductInfo = ({ product, onBuyNow }) => {
  const isMobile = useIsMobile(768);
  const price = Number(product.price || 0);
  const mrp = Number(product.mrp || 0);
  const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const descWordLimit = isMobile ? 40 : 60;

  return (
    <div className="flex flex-col gap-3 md:gap-5">
      {product.featured && (
        <span className="w-fit rounded-full bg-brand/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-brand border border-brand/30">
          ⚡ Bestseller
        </span>
      )}

      <div>
        <h1 className="text-xl font-bold leading-tight text-white sm:text-3xl md:text-[32px]">{product.title}</h1>
        <p className="mt-1 text-sm text-muted">Safe • Reliable • Long Lasting</p>
      </div>

      {/* Rating + brand/sku */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
        <span className="flex items-center gap-1 text-brand" aria-label="5 star rating">
          {[...Array(5)].map((_, i) => (
            <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          ))}
          <span className="ml-1 font-semibold text-white">4.9</span>
        </span>
        <span className="text-muted">(128 Reviews)</span>
        <span className="text-line">|</span>
        <span className="text-muted">Brand: <span className="font-medium text-white">{product.brand || 'FACTO'}</span></span>
      </div>

      {/* Price */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="text-3xl font-extrabold text-brand md:text-4xl">₹{price.toLocaleString()}</div>
        {mrp > price && (
          <>
            <div className="text-lg text-muted line-through md:text-xl">₹{mrp.toLocaleString()}</div>
            <span className="mb-1 rounded-lg bg-green-500/15 px-2 py-1 text-sm font-bold text-green-400">{discount}% OFF</span>
          </>
        )}
      </div>
      <p className="text-xs text-muted">Inclusive of all taxes</p>

      {/* Short description */}
      <div className="rounded-xl border border-line bg-card/60 p-3 md:p-4">
        <p className="text-sm leading-6 text-muted">
          {(product.description || 'This premium FACTO Electrical product is engineered with advanced technology to deliver safe, reliable and long-lasting performance. Manufactured from high-quality materials and rigorously tested to meet strict industry standards, it ensures maximum durability, shock resistance and flame retardancy — making it the trusted choice for modern homes, offices and commercial installations across India.').split(' ').slice(0, descWordLimit).join(' ')}...
        </p>
      </div>

      {/* Quantity + buttons */}
      {/* Buttons */}
      <div className="flex gap-3">
        <a
          href={`https://wa.me/917404704959?text=${encodeURIComponent(`Hi, I'm interested in ${product.title} (SKU: ${product.sku || product.id})`)}`}
          target="_blank"
          rel="noreferrer"
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#25D366] text-sm font-semibold text-white transition-all duration-250 hover:scale-[1.02] hover:bg-[#1ebd58]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </a>
        <button
          onClick={() => onBuyNow?.()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-brand bg-transparent text-sm font-semibold text-brand transition-all duration-250 hover:scale-[1.02] hover:bg-brand/10"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          Buy Now
        </button>
      </div>

      {/* Trust row */}
      <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-xl border border-line bg-card px-4 py-3 text-xs text-muted">
        {[
          { label: 'Free Delivery', icon: trustIcons.delivery },
          { label: 'Easy Return', icon: trustIcons.return },
          { label: '100% Genuine', icon: trustIcons.genuine },
          { label: 'Secure Payment', icon: trustIcons.secure },
        ].map((t) => (
          <span key={t.label} className="flex items-center gap-1.5">
            <span className="text-brand">{t.icon}</span>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ProductInfo;
